/* eslint-disable @typescript-eslint/only-throw-error */
import { type LoaderFunction, redirect } from 'react-router'
import { NotFound } from 'fejl'
import type { CreateLegendsRoutesOptions } from '@/features/legends/routes'
import type { LegendEditorViewModel } from '@/features/legends/LegendEditor/LegendEditorViewModel'
import { Id } from '@/infra/id'
import { links } from '@/utils/link'

/**
 * Data returned by {@link createLegendEditorViewLoader}. `null` when no
 * workspace is selected yet, so the authentication screen is showing.
 */
export type LegendEditorViewLoaderData = { vm: LegendEditorViewModel } | null

/**
 * Creates a loader function for the legend list that redirects users who may
 * not see the legends of any of their workspaces.
 *
 * @param options
 */
export function createLegendListViewLoader(
  options: CreateLegendsRoutesOptions,
): LoaderFunction {
  return async ({ request: { signal } }) => {
    const { workspace } = await options.waitForAuthResolution(signal)
    if (workspace && !options.legends.canViewLegends()) {
      throw redirect(links.welcome())
    }

    return null
  }
}

/**
 * Creates a loader function for the legend editor that provides the view model
 * for the route's legend, shows the route's tab, and redirects to the legend
 * list when the legend doesn't exist or the user may not see legends.
 *
 * @param options
 */
export function createLegendEditorViewLoader(
  options: CreateLegendsRoutesOptions,
): LoaderFunction {
  return async ({
    params,
    request: { signal },
  }): Promise<LegendEditorViewLoaderData> => {
    const { workspace } = await options.waitForAuthResolution(signal)
    if (!workspace) {
      return null
    }

    const internal = await options.legends.internal()
    const vm = await internal.acquireLegendEditorViewModel(
      Id(params.legendId),
      signal,
    )

    // A failed load runs again when the view's retry revalidates the route.
    // While the editor is open, a load that's still running carries on, like
    // when a tab is picked before the legend has loaded.
    const shouldFetch =
      !vm.legendVersion || vm.fetch.rejected || vm.shouldRefetch
    const isLoadingInOpenEditor = vm.fetch.pending && !vm.shouldRefetch
    if (shouldFetch && !isLoadingInOpenEditor) {
      // Started alongside the check rather than after it, so that opening a
      // legend doesn't wait on one more request.
      void vm.fetch(signal).catch(() => undefined)

      // A failed check leaves the decision to the load, whose failure the
      // view renders, so that the user learns why the legend didn't open.
      const canOpen = await vm.canOpen(signal).catch((err: unknown) => {
        if (err instanceof NotFound) {
          throw redirect(links.legends())
        }
        return true
      })

      if (!canOpen) {
        throw redirect(links.legends())
      }
    }

    if (vm.legendVersion && !vm.canView) {
      throw redirect(links.legends())
    }

    vm.showTabFromRoute(params.tab)
    return { vm }
  }
}
